import { StandardEvents } from '@shopify/events';

/**
 * Optional extended warranty chooser in the buy box.
 *
 * The choices are rendered by Liquid for the selected variant. When the shopper
 * picks another variant this element swaps in the freshly rendered choices and
 * keeps the tier they had chosen. product-form.js reads `getSelection()` when the
 * shopper adds to cart, so the warranty goes in with the fireplace.
 */
class FsaWarrantyPicker extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#scope = null;
  }

  /** @returns {HTMLInputElement | null} */
  #checked() {
    return this.querySelector('input[type="radio"]:checked');
  }

  /**
   * The warranty to add with the fireplace, or null when none is chosen.
   * @returns {{ variantId: string, properties: Record<string, string> } | null}
   */
  getSelection() {
    const variantId = this.#checked()?.dataset.variantId;
    if (this.hidden || !variantId) return null;

    /** @type {Record<string, string>} */
    const properties = {};
    if (this.dataset.covers) properties['Covers'] = this.dataset.covers;
    if (this.dataset.fireplaceVariantId) properties['_covers_variant'] = this.dataset.fireplaceVariantId;

    return { variantId, properties };
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const next = detail?.html?.querySelector(`fsa-warranty-picker[data-block-id="${this.dataset.blockId}"]`);
        if (!(next instanceof HTMLElement)) return;

        const tier = this.#checked()?.value ?? '';
        const detailsOpen = Boolean(this.querySelector('details')?.open);

        this.replaceChildren(...next.childNodes);

        const details = this.querySelector('details');
        if (details && detailsOpen) details.open = true;
        this.hidden = next.hidden;
        this.dataset.productId = next.dataset.productId ?? '';
        this.dataset.fireplaceVariantId = next.dataset.fireplaceVariantId ?? '';
        this.dataset.covers = next.dataset.covers ?? '';

        if (!tier) return;
        const same = /** @type {HTMLInputElement | null} */ (
          [...this.querySelectorAll('input[type="radio"]')].find((input) => input instanceof HTMLInputElement && input.value === tier)
        );
        if (same) same.checked = true;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[fsa-warranty-picker] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('fsa-warranty-picker')) {
  customElements.define('fsa-warranty-picker', FsaWarrantyPicker);
}
